import { Box, ButtonBase, SvgIcon, Typography } from "@mui/material";
import {
  LockOutlined,
  LockOpenOutlined,
  PowerOutlined,
  PowerOffOutlined,
} from "@mui/icons-material";

const BATTERY_STATE = {
  0: { label: "Empty", color: "#667085", border: "#E3E8EE" },
  1: { label: "Charging", color: "#0E7C86", border: "#E3E8EE" },
  2: { label: "Fully charged", color: "#1B7F43", border: "#E3E8EE" },
  3: { label: "Fault", color: "#B54708", border: "#F9D8A8" },
  5: { label: "Critical fault", color: "#B42318", border: "#F5C2C2" },
};

const UNKNOWN_STATE = { label: "Unknown", color: "#667085", border: "#E3E8EE" };

const TEAL = "#0E7C86";

const DoorCard = ({
  data,
  index,
  selectedDoor,
  onClick,
  disabled,
  batteryState,
  chargerOn,
}) => {
  const { canIds, doorStatus, soc, voltage } = data;
  const canId = canIds?.[index];
  const hasBattery = Boolean(canId);
  const selected = selectedDoor === index;
  const doorOpen = doorStatus?.[index] === 1;
  const state = BATTERY_STATE[batteryState] || UNKNOWN_STATE;
  const isFault = batteryState === 3 || batteryState === 5;

  let borderColor = "#E3E8EE";
  if (isFault) borderColor = state.border;
  if (selected) borderColor = TEAL;

  return (
    <Box sx={{ position: "relative", pt: 1.25, opacity: disabled ? 0.55 : 1 }}>
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1,
          px: 1,
          py: 0.1,
          borderRadius: 999,
          border: "1px solid",
          borderColor: "#D0D5DD",
          bgcolor: selected ? TEAL : "#FFFFFF",
          color: selected ? "#FFFFFF" : "#344054",
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1.6,
          whiteSpace: "nowrap",
        }}
      >
        Door {index + 1}
      </Box>

      <ButtonBase
        disabled={disabled}
        onClick={() => onClick(index)}
        aria-pressed={selected}
        sx={{
          display: "flex",
          width: "100%",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          textAlign: "left",
          px: 2,
          pt: 2,
          pb: 1.5,
          borderRadius: 3,
          border: "1.5px solid",
          borderColor,
          bgcolor: selected ? "#F0FAFA" : "#FFFFFF",
          boxShadow: selected ? "0 0 0 3px rgba(14,124,134,.14)" : "none",
          transition: "border-color .2s, background-color .2s, box-shadow .2s",
          "&.Mui-focusVisible": {
            outline: `2px solid ${TEAL}`,
            outlineOffset: 2,
          },
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {hasBattery ? (
            <>
              <Typography
                noWrap
                sx={{ fontSize: 15, fontWeight: 600, color: "#101828" }}
              >
                {canId}
              </Typography>
              <Typography
                sx={{
                  mt: 0.25,
                  fontSize: 13,
                  color: "#475467",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {voltage?.[index] ?? "NA"}V / {soc?.[index] ?? "NA"}%
              </Typography>
            </>
          ) : (
            <Typography sx={{ fontSize: 14, color: "#98A2B3" }}>
              No battery
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            {chargerOn ? (
              <PowerOutlined sx={{ fontSize: 20, color: "#1B7F43" }} />
            ) : (
              <PowerOffOutlined sx={{ fontSize: 20, color: "#98A2B3" }} />
            )}
            {doorOpen ? (
              <OpenLockIcon
                titleAccess="Door open"
                sx={{ fontSize: 22, color: "#B54708" }}
              />
            ) : (
              <LockOutlined sx={{ fontSize: 20, color: "#475467" }} />
            )}
          </Box>
          {batteryState !== 0 && (
            <Typography
              sx={{
                mt: 0.5,
                fontSize: 12,
                fontWeight: 600,
                color: state.color,
              }}
            >
              {state.label}
            </Typography>
          )}
        </Box>
      </ButtonBase>
    </Box>
  );
};

export default DoorCard;

const OpenLockIcon = (props) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path d="M16 3.25C14.7402 3.25 13.532 3.75045 12.6412 4.64124C11.7504 5.53204 11.25 6.74022 11.25 8V10.25H6C5.27065 10.25 4.57118 10.5397 4.05546 11.0555C3.53973 11.5712 3.25 12.2707 3.25 13V18C3.25 18.7293 3.53973 19.4288 4.05546 19.9445C4.57118 20.4603 5.27065 20.75 6 20.75H13C13.7293 20.75 14.4288 20.4603 14.9445 19.9445C15.4603 19.4288 15.75 18.7293 15.75 18V13C15.75 12.2707 15.4603 11.5712 14.9445 11.0555C14.4288 10.5397 13.7293 10.25 13 10.25H12.75V8C12.75 7.13805 13.0924 6.3114 13.7019 5.7019C14.3114 5.09241 15.138 4.75 16 4.75C16.862 4.75 17.6886 5.09241 18.2981 5.7019C18.9076 6.3114 19.25 7.13805 19.25 8C19.25 8.19891 19.329 8.38968 19.4697 8.53033C19.6103 8.67098 19.8011 8.75 20 8.75C20.1989 8.75 20.3897 8.67098 20.5303 8.53033C20.671 8.38968 20.75 8.19891 20.75 8C20.75 6.74022 20.2496 5.53204 19.3588 4.64124C18.468 3.75045 17.2598 3.25 16 3.25ZM14.25 13V18C14.25 18.3315 14.1183 18.6495 13.8839 18.8839C13.6495 19.1183 13.3315 19.25 13 19.25H6C5.66848 19.25 5.35054 19.1183 5.11612 18.8839C4.8817 18.6495 4.75 18.3315 4.75 18V13C4.75 12.6685 4.8817 12.3505 5.11612 12.1161C5.35054 11.8817 5.66848 11.75 6 11.75H13C13.3315 11.75 13.6495 11.8817 13.8839 12.1161C14.1183 12.3505 14.25 12.6685 14.25 13Z" />
  </SvgIcon>
);
